import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';

import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { BillingCycleOutput, BillingCycleStatusEnum } from '@/graphql/contracts/_generated_/contracts';
import { BillingCycleStatusFormatted } from '@/config/statuses.config';
import { NumberUtils } from '@/utils/numbers.utils';
import { AgGridSelectableFilterOwnProps } 
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';
import { BillingCycleFilterOptions } from '../filters/BillingCycleFilterOptions';
import { BillingCycleFilterComparators } from '../filters/BillingCycleFilterComparators';

export const getBillingCyclesColumns = (): ColDef<BillingCycleOutput>[] => [
  {
    colId: 'stockCode',
    headerName: 'Stock Code',
    field: 'stockCode',
    width: 330,
    minWidth: 330,
    cellRenderer: ({ data }: ICellRendererParams<BillingCycleOutput>) => {
      return `${data?.stockCode} - ${data?.stockName}`;
    },
    filter: 'SelectableFilter',
    filterParams: (): AgGridSelectableFilterOwnProps<BillingCycleOutput> => ({
      optionsOrGetterFunc: BillingCycleFilterOptions.getStockCodeFilterOptions,
      comparator: BillingCycleFilterComparators.stockCodeComparator,
      rerenderOnNewRowsLoaded: true,
    }),
  },
  {
    colId: 'billingDate',
    headerName: 'Billing Date',
    field: 'billingDate',
    width: 150,
    minWidth: 150,
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
        if (!cellValue) return -1;
        const cellDate = new Date(cellValue);
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
    valueFormatter: ({ value }: ValueFormatterParams) => {
      if (value && !Number.isNaN(Date.parse(value))) {
        return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
      }
      return '';
    }
  },
  {
    colId: 'startDate',
    headerName: 'BC Start Date',
    field: 'startDate',
    width: 150,
    minWidth: 150,
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
        if (!cellValue) return -1;
        const cellDate = new Date(cellValue);
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
    valueFormatter: ({ value }: ValueFormatterParams) => {
      if (value && !Number.isNaN(Date.parse(value))) {
        return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
      }
      return '';
    },
    sort: 'desc',
    sortIndex: 2,
    comparator:(date1, date2) => {
      const date1Ms = Date.parse(date1);
      const date2Ms = Date.parse(date2);

      if (Number.isNaN(date1Ms) && Number.isNaN(date2Ms)) {
        return 0;
      }
      if (Number.isNaN(date1Ms)) {
        return -1;
      }
      if (Number.isNaN(date2Ms)) {
        return 1;
      }

      return date1Ms - date2Ms;
    }
  },
  {
    colId: 'endDate',
    headerName: 'BC End Date',
    field: 'endDate',
    width: 150,
    minWidth: 150,
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
        if (!cellValue) return -1;
        const cellDate = new Date(cellValue);
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
    valueFormatter: ({ value }: ValueFormatterParams) => {
      if (value && !Number.isNaN(Date.parse(value))) {
        return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
      }
      return '';
    }
  },
  {
    colId: 'processingStatus',
    headerName: 'Processing Status',
    field: 'processingStatus',
    width: 160,
    minWidth: 160,
    filter: 'SelectableFilter',
    filterParams: (): AgGridSelectableFilterOwnProps<BillingCycleOutput> => ({
      optionsOrGetterFunc: BillingCycleFilterOptions.getProcessingStatusFilterOptions,
      comparator: BillingCycleFilterComparators.processingStatusComparator,
      rerenderOnNewRowsLoaded: true,
    }),
    valueFormatter: ({ value }: ValueFormatterParams<BillingCycleOutput, BillingCycleStatusEnum>) => {
      return value != null ? BillingCycleStatusFormatted[value] : '';
    },
  },
  {
    colId: 'statusReason',
    headerName: 'Status Reason',
    field: 'statusReason',
    width: 230,
    filter: 'SelectableFilter',
    filterParams: (): AgGridSelectableFilterOwnProps<BillingCycleOutput> => ({
      optionsOrGetterFunc: BillingCycleFilterOptions.getStatusReasonFilterOptions,
      comparator: BillingCycleFilterComparators.statusReasonComparator,
      rerenderOnNewRowsLoaded: true,
    }),
  },
  {
    colId: 'consolidationCriteriaCode',
    headerName: 'Consolidation Criteria Code',
    field: 'consolidationCriteriaCode',
    width: 200,
    hide: true,
  },
  {
    colId: 'billingValue',
    headerName: 'Billing Value',
    field: 'billingValue',
    width: 150,
    filter: 'agNumberColumnFilter',
    filterParams: {
      allowedCharPattern: '\\d\\-\\.',
      numberParser: (text: string) => {
        return NumberUtils.parseFloatWithDecimal(text);
      },
    },
    valueFormatter: ({ value }: ValueFormatterParams) => {
      return NumberUtils.prettifyWithRound(value);
    },
    cellStyle: { textAlign: 'right' },
  },
  {
    colId: 'contractLineID',
    headerName: 'Contract Line ID',
    field: 'contractLineID',
    width: 320,
    sort: 'desc',
    sortIndex: 1,
    filter: 'SelectableFilter',
    filterParams: (): AgGridSelectableFilterOwnProps<BillingCycleOutput> => ({
      optionsOrGetterFunc: BillingCycleFilterOptions.getContractLineIDFilterOptions,
      comparator: BillingCycleFilterComparators.contractLineIDComparator,
      rerenderOnNewRowsLoaded: true,
    }),
  },
];
